/**
 * API-Schicht des Widerruf-Registers.
 *
 * Eigene Datei, weil der Vorgang **drei Bereichen gemeinsam** gehört: Vertrieb
 * bearbeitet, Kundenbetreuung nimmt zur Kenntnis, Finance schliesst ab. Ein
 * Anbau an `api/kb.ts` oder an die Vertriebs-Schicht hätte ihn einem Bereich
 * zugeschlagen, dem er nicht allein gehört.
 */

import type { AnpassungImErgebnis } from "./vertragsanpassung";
import { callBackend } from "./client";

/**
 * Woraus die Widerrufsfrist stammt — Vorrang `gepflegt` → `vertrag` → `termin`.
 *
 * 🔴 `gepflegt` kam am 14.09.2026 dazu: `kb_vereine_stamm.widerrufsfrist` (aus
 * HubSpot gespiegelt) wurde vom Register **nicht gelesen**, obwohl sie die
 * einzige der drei Quellen ist, die nachgepflegt wird. Zwei Fälle galten
 * deshalb als „außerhalb der Frist", die innerhalb lagen. Begründung und
 * Messung in `src/lib/widerrufsfrist.ts`.
 */
export type FristQuelle = "gepflegt" | "vertrag" | "termin" | "keine" | "kein_recht";

/** Die Quelle im Klartext — für Anzeige, nicht für Vergleiche. */
export const FRIST_QUELLE_LABEL: Record<FristQuelle, string> = {
  gepflegt: "gepflegte Widerrufsfrist am Verein",
  vertrag: "festes Datum aus der Zusatzvereinbarung",
  termin: "gerechnet ab der Vereinseinrichtung",
  keine: "nicht bestimmbar",
  // 🆕 26.10.2026 (E1 ②): Bestellung ab 23.10. ohne Satz 3.
  kein_recht: "kein Widerrufsrecht vereinbart"
};

export type WiderrufZustand =
  | "eingegangen" | "in_bearbeitung" | "frist_abgelaufen"
  | "ergebnis_gemeldet" | "wartet_auf_bestaetigung" | "abgeschlossen" | "storniert";

export type WiderrufErgebnis =
  | "besteht" | "zurueckgenommen" | "nicht_erreicht" | "vertrag_angepasst";

export type QuittungPartei = "vertrieb" | "kb" | "finance";

export type WiderrufFall = {
  fall_id: string;
  company_id: string;
  verein: string;
  closing_id: string;
  eingang_am: string;
  /** J-3 (27.10.2026): `eingang_am` ist der Erfassungstag, das Eingangsdatum fehlt. */
  eingang_ungeprueft?: boolean;
  eingang_weg: string;
  eingang_absender: string;
  wortlaut: string;
  erfasst_von: string;
  erfasst_am: string;
  vertragsstart: string;
  frist_ende: string;
  frist_quelle: FristQuelle;
  recht_offen: boolean;
  bearbeiter: string;
  frist_bis: string;
  angefasst_am: string;
  ergebnis: string;
  ergebnis_notiz: string;
  ergebnis_beleg: string;
  ergebnis_von: string;
  ergebnis_am: string;
  q_vertrieb_von: string; q_vertrieb_am: string;
  q_kb_von: string; q_kb_am: string;
  q_finance_von: string; q_finance_am: string;
  /**
   * Der VIERTE Riegel (§11, Justin 10.09.2026): hat der Verein eine
   * Bestätigung seines Widerrufs bekommen?
   *
   * 🔑 **Keine vierte Quittung.** Die drei Quittungen sind INTERNE
   * Kenntnisnahmen; dieser Riegel ist eine Handlung **nach aussen**, an den
   * Verein. Deshalb steht er in der Ansicht auch nicht in der Quittungs-Reihe.
   *
   * 🔴 **Seit 15.09.2026 hängt er am ERGEBNIS** (Entscheid Justin): fällig nur
   * bei „besteht" und „nicht erreicht" — dort endet der Vertrag. Bei
   * „zurückgenommen" und „Vertrag angepasst" bleibt er, und eine Bestätigung
   * wäre die Bestätigung einer Sache, die nicht eingetreten ist. Vorher war er
   * eine *Eingangs*bestätigung für jeden Fall, 📏 und wurde bei **0 von 11**
   * gesetzt. Verschickt wird sie vorerst **von Hand** — hier steht nur der
   * Haken mit Beleg.
   */
  bestaetigung_an_verein_am: string;
  bestaetigung_an_verein_von: string;
  /** Mail, Datum, Kanal — Pflicht beim Setzen. */
  bestaetigung_beleg: string;
  bestaetigung_text: string;
  /**
   * 🆕 J15a (16.10.2026) — „Bestätigung entfällt", nur Altfälle (Eingang vor
   * dem 17.10.2026). Getrennt vom Versanddatum oben; leer = nicht entfallen.
   */
  bestaetigung_entfaellt_am: string;
  bestaetigung_entfaellt_von: string;
  bestaetigung_entfaellt_grund: string;
  /**
   * 🆕 Migration 0115 (17.09.), gelesen seit 20.09. — der VE-Termin, wie er BEI
   * DER ERFASSUNG stand. `""` bei allen Fällen davor.
   */
  ve_datum_bei_erfassung: string;
  abgeschlossen_am: string;
  storniert_am: string;
  storno_grund: string;
  /**
   * Die fünf HubSpot-Aufgaben-IDs. `""` heisst: **nicht angelegt** — entweder
   * weil sie zu diesem Fall nicht gehört (die Vertriebs-Aufgabe entsteht nur
   * bei offenem Widerrufsrecht) oder weil das Anlegen scheiterte. Welches von
   * beidem, sagt `hs_fehler`.
   *
   * 🔑 Sie stehen hier, damit die Detailansicht sie ZEIGEN kann. `fallAnsicht`
   * hat sie immer mitgeliefert (`...f`) — sie fehlten nur im Typ, und was im
   * Typ fehlt, kommt in der Oberfläche nicht vor.
   */
  hs_task_vertrieb: string;
  hs_task_finance: string;
  hs_task_frist: string;
  hs_task_umsetzung: string;
  hs_task_kb: string;
  hs_task_bestaetigung: string;
  /** Klartext, wenn eine HubSpot-Aufgabe nicht angelegt werden konnte. */
  hs_fehler: string;
  zustand: WiderrufZustand;
  offene_quittungen: QuittungPartei[];
  /**
   * §11.2① — **ab dem Anlegen `true`**, bis die Bestätigung gesetzt ist.
   *
   * 🔑 Das ist die Gegenleistung dafür, dass der MELDER erst nach der letzten
   * Quittung startet (Entscheid Justin 10.09.). Ohne diese Anzeige wäre eine
   * fehlende Widerrufsbestätigung bis zum Ende des Vorgangs unsichtbar.
   *
   * ⚠️ `true` heisst **offen**, nicht **überfällig**. Die beiden Felder darunter
   * sagen, ob eine Uhr läuft — *eine Anzeige, die beides vermischt, macht aus
   * einer Lücke eine Mahnung und aus einer Mahnung Gewöhnung.*
   */
  offene_bestaetigung: boolean;
  /** Seit wann alle drei Quittungen stehen. `""` = die Uhr läuft nicht. */
  bestaetigung_uhr_ab: string;
  /**
   * Werktage seit der letzten Quittung ohne Bestätigung. `null` heisst **keine
   * Uhr** — und das ist etwas anderes als `0` („heute quittiert").
   */
  bestaetigung_werktage: number | null;
  /**
   * Werktage bis `frist_bis` — negativ, wenn die Frist vorbei ist, `0` heisst
   * **heute fällig**, `null` heisst **es läuft keine Uhr**.
   *
   * 🔑 Der Wert kommt vom Server, weil dort die **Feiertagsliste** liegt. Die
   * erste Fassung dieser Ansicht zählte Kalendertage — ehrlich beschriftet,
   * aber über ein Wochenende meldete sie drei Tage, wo der Vertrieb einen
   * Werktag hat. *Die Zahl gehört dorthin, wo die Grundlage liegt.*
   */
  frist_werktage: number | null;
  /**
   * 🔴 Die eingefrorene Frist ist ÄLTER als der VE-Termin, den es bei der
   * Erfassung schon gab — sie war der Platzhalter (Abschluss + 14 Tage).
   *
   * 📏 Gemessen 20.09.2026: 3 von 19 Fällen, **0 davon mit anderer
   * Aufnahmeentscheidung**. 🔑 *Der Fehler ist latent, nicht wirksam — und
   * genau deshalb wird er angezeigt statt repariert.*
   */
  frist_platzhalter: boolean;
  ergebnis_label: string;
};

export type RisikoEintrag = {
  companyId: string;
  verein: string;
  vertragsstart: string;
  einrichtung: string;
  fristEnde: string;
  fristQuelle: FristQuelle;
  lage: "kein_termin" | "nach_start" | "am_start" | "ok";
  kundenbetreuer: string;
};

export type WiderrufUebersicht = {
  heute: string;
  faelle: WiderrufFall[];
  /**
   * 🆕 04.10.2026 — die `company_id` aller **Testvereine** (Glattzug E16).
   *
   * Ihre Zeilen bleiben in `faelle` und in `rechnungsrisiko.eintraege`, stehen
   * aber blass am Ende und zählen in **keiner** Kachel mit. Die Zählung in
   * `zaehlung` lässt sie bereits aus.
   * 🔴 Quelle ist das Register `ve_testverein`, **nicht** der Name.
   * 📏 Am 04.10.2026 betraf das hier **0 von 36** Fällen — die Regel steht
   * trotzdem, weil der erste Fall sonst der ist, an dem sie vergessen wird.
   */
  testvereine: string[];
  zaehlung: {
    gesamt: number;
    eingegangen: number;
    in_bearbeitung: number;
    frist_abgelaufen: number;
    ergebnis_gemeldet: number;
    ohne_aufgabe: number;
  };
  rechnungsrisiko: {
    eintraege: RisikoEintrag[];
    zaehlung: Record<string, number>;
    bisDatum: string;
  } | null;
  risiko_fehler: string;
};

export type FristLage = {
  vertragsstart: string;
  fristEnde: string;
  quelle: FristQuelle;
  /**
   * 🔴 Klartext, wenn die **gepflegte** Frist am Verein und das **feste Datum**
   * der Zusatzvereinbarung auseinanderlaufen. `""` = einig.
   *
   * 🔑 Er steht in der **Vorschau der Meldemaske**, also *vor* dem Speichern —
   * genau dort, wo ein Mensch noch etwas ändern kann; danach ist die Frist am
   * Fall eingefroren. 📏 Bei Friedrichsfeld und Vöhringen lagen 31 bzw. 33 Tage
   * zwischen den beiden Angaben, drei Wochen lang unbemerkt.
   */
  abweichung: string;

  // ── 🆕 20.09.2026 · die AUFGELÖSTE Antwort, aus `ve_widerrufsfrist` ───────
  // 🔑 Bis heute stand hier nur das Datum und ein Spaltenname in Klammern
  // („gepflegt"). Das ist keine Herkunft. Diese Felder tragen den ganzen Satz,
  // mit Beleg — und sagen, wenn die Antwort auf einer Auslegung ruht oder eine
  // Quelle nie gelesen wurde.
  // ⚠️ Sie sind **Anzeige, nicht Rechnung**: massgeblich bleiben `fristEnde`
  // und `quelle`, bis die Umstellung entschieden ist.

  /** `norm` | `datum` | `keine` | `individuell`; `""` = nie aufgelöst. */
  rechtArt: string;
  /** Das aufgelöste Fristende — kann von `fristEnde` abweichen. */
  rechtFristBis: string;
  herkunftssatz: string;
  /** `""` | `ungeprueft` */
  vorbehalt: string;
  auslegung: string;

  verein: string;
  kundenbetreuer: string;
  vertriebler: string;
  vorstandOnboarding: string;
  trainereinfuehrung: string;
};

export type VerlaufEintrag = { art: string; text: string; akteur: string; ms: number };

/** Antwortform aller schreibenden Vorgänge — Fall + Hinweise im Klartext. */
export type VorgangAntwort = { fall: WiderrufFall; hinweise: string[] };

export const widerrufUebersicht = (alle = false) =>
  callBackend<WiderrufUebersicht>("/api/v1/widerruf/uebersicht", { alle });

/**
 * 🆕 **Die Zeitraum-Liste** (01.10.2026) — deckungsgleich zur Closingliste.
 *
 * ⚠️ Zeitraum-Anker ist `eingang_am`, und sie führt den **ganzen** Bestand im
 * Zeitraum — auch Abgeschlossene und Stornierte. 🔑 *Eine Zeitraum-Liste, die
 * nur Offenes zeigt, beantwortet „was liegt an" und sieht aus, als
 * beantwortete sie „was war".*
 */
export type WiderrufListe = {
  heute: string;
  zeilen: WiderrufFall[];
  zeitraum: {
    start_date: string; end_date: string; label: string; slug: string;
    previous: { start_date: string; end_date: string } | null;
  };
  ausserhalb: number;
  ohne_datum: number;
};

export const widerrufListe = (p: { range?: string; start_date?: string; end_date?: string }) =>
  callBackend<WiderrufListe>("/api/v1/widerruf/liste", p);

/**
 * ⚠️ `bestaetigung_versand` kommt MIT dem Fall, nicht aus einer Rolle hier.
 * 🔑 *Zwei Orte, die dieselbe Befugnis berechnen, weichen irgendwann
 * voneinander ab — und der sichtbare gewinnt.* Der Server entscheidet, das
 * Frontend zeigt nur.
 * 📌 `eingerichtet` ist getrennt von `darf`: fehlt der Gmail-Schlüssel, sieht
 * auch der Berechtigte den GRUND statt eines Knopfes, der nichts tut.
 */
export type BestaetigungVersand = {
  darf: boolean;
  wer: string;
  absender: string;
  eingerichtet: boolean;
  grund: string;
};

export const widerrufFall = (fallId: string) =>
  callBackend<{
    fall: WiderrufFall;
    verlauf: VerlaufEintrag[];
    bestaetigung_versand?: BestaetigungVersand;
  }>("/api/v1/widerruf/fall", { fall_id: fallId });

// ⛔ Für `/api/v1/widerruf/zuVerein` gibt es hier ABSICHTLICH keinen Aufrufer.
// Der Endpunkt existiert (alle Widerrufe eines Vereins — für die Vereinsakte),
// aber ein Frontend-Wrapper ohne Verwendung wäre ein toter Export, und der
// Wächter `tote-exporte` meldet ihn zu Recht. 🔑 *Eine Funktion, die niemand
// ruft, ist kein Vorrat, sondern eine Behauptung über die Zukunft.* Wenn die
// Vereinsakte sie braucht, kommt sie mit ihrem Aufrufer zusammen zurück.

export const widerrufFristLage = (companyId: string) =>
  callBackend<{ lage: FristLage; eingang_wege: string[]; ergebnisse: string[] }>(
    "/api/v1/widerruf/fristLage", { company_id: companyId }
  );

export const widerrufErfassen = (e: {
  company_id: string; closing_id?: string; eingang_am: string; eingang_weg: string;
  eingang_absender?: string; wortlaut?: string; bearbeiter?: string;
}) => callBackend<VorgangAntwort>("/api/v1/widerruf/erfassen", e);

export const widerrufUebernehmen = (fallId: string) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/uebernehmen", { fall_id: fallId });

export const widerrufBearbeiter = (fallId: string, bearbeiter: string) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/bearbeiter", { fall_id: fallId, bearbeiter });

export const widerrufErgebnis = (e: {
  fall_id: string; ergebnis: WiderrufErgebnis; notiz?: string; beleg?: string;
  /** 🆕 05.10.2026 (K9): bei „Vertrag angepasst“ die Anpassung im selben Schritt — ersetzt die Notiz. */
  anpassung?: AnpassungImErgebnis;
}) => callBackend<VorgangAntwort>("/api/v1/widerruf/ergebnis", e);

export const widerrufQuittieren = (fallId: string, partei: QuittungPartei) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/quittieren", { fall_id: fallId, partei });

export const widerrufErgebnisZurueck = (fallId: string, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/ergebnisZurueck", { fall_id: fallId, grund });

export const widerrufQuittungZurueck = (fallId: string, partei: QuittungPartei, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/quittungZurueck", { fall_id: fallId, partei, grund });

export const widerrufStornieren = (fallId: string, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/stornieren", { fall_id: fallId, grund });

/**
 * Den vierten Riegel setzen (§11). `am` ist optional — leer heisst heute.
 *
 * ⚠️ **Der Beleg ist Pflicht**, und die Prüfung sitzt im Worker, nicht hier.
 * *Ein ausgeblendeter Knopf ist keine Berechtigung, und ein Pflichtfeld im
 * Formular ist keine Pflicht.*
 */
/**
 * Der vierte Riegel von Hand — für Bestätigungen, die ausserhalb der App
 * rausgingen.
 *
 * ⚠️ Seit dem 18.09.2026 verlangt der Server **`mailtext`** (den Wortlaut) und
 * **`erklaert`** (das ausdrückliche „abgeschickt"). 🔑 *Ein Wortlaut lässt sich
 * nicht beibringen, ohne dass es ihn gibt — eine Absichtserklärung schon.*
 */
export const widerrufBestaetigung = (
  fallId: string, beleg: string, mailtext: string, erklaert: boolean, am = ""
) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/bestaetigung", {
    fall_id: fallId, beleg, am, mailtext, erklaert
  });

export const widerrufBestaetigungZurueck = (fallId: string, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/bestaetigungZurueck", { fall_id: fallId, grund });

/** J15a — „Bestätigung entfällt" für einen Altfall. Nur BL/Admin; der Server prüft Stichtag und Frist. */
export const widerrufBestaetigungEntfaellt = (fallId: string, grund: string, keineVerlangt: boolean) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/bestaetigungEntfaellt", {
    fall_id: fallId, grund, keine_verlangt: keineVerlangt
  });

export const widerrufBestaetigungEntfaelltZurueck = (fallId: string, grund: string) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/bestaetigungEntfaelltZurueck", { fall_id: fallId, grund });

/** Eingang vor diesem Tag = Altfall (Entscheid Justin 16.10.2026). Spiegel von `ENTFAELLT_STICHTAG`. */
export const ENTFAELLT_STICHTAG = "2026-10-17";

/**
 * Der Entwurf der Widerrufsbestätigung. **Schreibt nichts, sendet nichts.**
 *
 * > **Justin, 15.09.2026:** *„ich drücke auf widerruf bestätigen - dann öffnet
 * > sich die Vorlage die ich dann nochmal prüfen und final abschicken kann."*
 *
 * 🔑 ***Ein Knopf, der eine Erklärung an einen Vertragspartner sofort absetzt,
 * hat keinen Rückweg.*** Jede andere Handlung im Register hat einen — eine
 * Mail nicht. Deshalb zwei Aufrufe.
 */
export const widerrufBestaetigungEntwurf = (fallId: string) =>
  callBackend<{
    an: string; betreff: string; text: string;
    unterzeichner: string; absender: string; hinweise: string[];
  }>("/api/v1/widerruf/bestaetigungEntwurf", { fall_id: fallId });

/**
 * Absenden — mit dem Text, der auf dem Bildschirm stand.
 *
 * ⚠️ Der Server baut den Text NICHT neu. 🔑 *Ein Server, der den geprüften
 * Text noch einmal selbst erzeugt, verschickt etwas, das niemand gelesen hat*
 * — und die Prüfung wäre eine Zierde.
 */
/**
 * Die PROBE des Versandwegs. **Fasst keinen Fall an, kann keinen Verein
 * erreichen** — es gibt keinen Empfänger-Parameter, die Testmail geht an das
 * Absender-Postfach selbst.
 *
 * 🔑 *Eine Prüffunktion mit freiem Empfängerfeld ist irgendwann der bequemste
 * Weg, eine Mail zu verschicken — und dann ist sie keine Prüffunktion mehr.*
 *
 * `mail: false` prüft nur den Token-Tausch (also die Delegierung), `true`
 * schickt zusätzlich eine Probe ans eigene Postfach.
 */
export const widerrufBestaetigungProbe = (mail: boolean) =>
  callBackend<{
    ok: boolean; konto: string; postfach: string; quelle: string; meldung: string;
    /** Die Überschrift — kommt vom Server, aus derselben Quelle wie `meldung`. */
    titel: string;
  }>("/api/v1/widerruf/bestaetigungProbe", { mail });

export const widerrufBestaetigungSenden = (
  fallId: string, an: string, betreff: string, text: string
) =>
  callBackend<VorgangAntwort>("/api/v1/widerruf/bestaetigungSenden", {
    fall_id: fallId, an, betreff, text
  });

// ═══════════════════════════════════════════════════════════════════════════
// DER ABGLEICH — Widerruf ⇄ HubSpot (§2–§7)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Was der Abgleich mit einem Verein vorhat.
 *
 * 🔑 **`melden` ist kein Zwischending zwischen „stimmt" und „schreiben".** Es
 * ist die dritte echte Lage: *hier läuft etwas auseinander, und der Zielzustand
 * lässt sich nicht aus dem Register ableiten.* Genau die Lage von
 * `W-2026-0007` — HubSpot trägt den Widerruf, das Register hat kein Ergebnis.
 */
export type AbgleichLage = "stimmt" | "schreiben" | "melden" | "unberuehrt";

export type AbgleichEintrag = {
  companyId: string;
  verein: string;
  fallId: string;
  lage: AbgleichLage;
  /** Der Befund OHNE die Feldliste — fuer die Spalte am Bildschirm. */
  befund: string;
  grund: string;
  aenderungen: { feld: string; von: string; nach: string }[];
};

export type AbgleichStats = {
  geprueft: number;
  stimmt: number;
  geschrieben: number;
  gemeldet: number;
  unberuehrt: number;
  fehler: string[];
  eintraege: AbgleichEintrag[];
  modus: "off" | "dryrun" | "on";
  dryRun: boolean;
};

/**
 * Den Abgleich **trocken** rechnen lassen.
 *
 * ⚠️ Der Aufruf liest je Verein LIVE aus HubSpot — er ist langsam und gehört
 * deshalb hinter einen Knopf, nicht in den Seitenaufbau.
 * 🔑 *Er ANTWORTET, er schreibt nicht.* Ein Knopf, der von der Oberfläche aus
 * in HubSpot schreibt, wäre genau der Schreibweg, den dieser Bau abschafft.
 */
/**
 * Anzeigenamen je Aufzählungs-Feld, **live aus den Property-Options des
 * Portals** (18.09.2026, Entwurf v3 §3).
 *
 * ⚠️ Ein Feld fehlt hier, wenn HubSpot keine Optionen lieferte — dann zeigt der
 * Schirm den internen Wert. 🔑 *Ein fehlender Schlüssel ist eine Auskunft
 * („nicht übersetzbar"), ein leerer wäre eine Behauptung („keine Übersetzung").*
 */
export type WertLabels = Record<string, Record<string, string>>;

/** 🆕 05.10.2026 (V07·H2) — das abgelegte Ergebnis des letzten 06:00-Laufs. */
export type AbgleichLauf = {
  heute: string; ms: number;
  geprueft: number; stimmt: number; geschrieben: number; gemeldet: number; unberuehrt: number;
  fehler: number; dryRun: boolean;
  abweichend: Array<{ fallId: string; verein: string; lage: string; grund: string }>;
};

export const widerrufAbgleichLetzter = () =>
  callBackend<{ lauf: AbgleichLauf | null; heute: string }>("/api/v1/widerruf/abgleichLetzter", {});

export const widerrufAbgleich = (companyId = "") =>
  callBackend<{ abgleich: AbgleichStats; heute: string; wert_labels?: WertLabels }>(
    "/api/v1/widerruf/abgleich", companyId ? { company_id: companyId } : {}
  );

/** Klartext der vier Felder — die internen Namen sagen niemandem etwas. */
export const ABGLEICH_FELD_LABEL: Record<string, string> = {
  hs_lead_status: "Lead-Status",
  lifecyclestage: "Lifecycle",
  gekundigt_am_: "gekündigt am",
  gekundigt_zum_: "gekündigt zum"
};

// ═══════════════════════════════════════════════════════════════════════════
// DIE WIDERRUFSQUOTE JE CLOSER (§12)
// ═══════════════════════════════════════════════════════════════════════════

export type CloserQuote = {
  /**
   * Der Schlüssel, wie er in `Closer_Resolved` steht — 📏 am 14.09.2026 eine
   * **Personalnummer** (`P-004`), kein Name. Gruppierung und Filter hängen
   * daran; er bleibt deshalb sichtbar, auch wenn `name` gefüllt ist.
   */
  closer: string;
  /** Der aufgelöste Anzeigename aus dem Personalstamm. `""` = nicht gefunden. */
  name: string;
  /** Alle Closings im Zeitraum — **einschliesslich** der widerrufenen. */
  nenner: number;
  zaehler: number;
  /** `null` bei Nenner 0. */
  quote: number | null;
  /** Wie viele des Nenners ihre Closer-Herkunft nicht belegen können. */
  ohneHerkunft: number;
};

export type VerlaufPunkt = { monat: string; nenner: number; zaehler: number; quote: number | null };

export type Zeitraum = { von: string; bis: string; art: string; label: string };

export type QuotenAntwort = {
  bericht: {
    von: string; bis: string;
    zeilen: CloserQuote[];
    /** Die Bezugslinie — *„wie hoch ist die im Allgemeinen"*. */
    gesamt: CloserQuote;
    rohMinusEcht: number;
  };
  verlauf: VerlaufPunkt[];
  /**
   * 🆕 25.09.2026 — der Verlauf JE Closer, Schluessel ist die Personalnummer.
   * Fuer das Linien-Diagramm des Entwurfs v4: eine Linie fuer die gewaehlte
   * Person, eine fuer alle zusammen.
   */
  verlaufJe: Record<string, VerlaufPunkt[]>;
  /** `w` = Wochenfaecher, `m` = Monatsfaecher. */
  granular: "m" | "w";
  /** Echte Widerrufe im ganzen Bestand (nicht nur im Zeitraum). */
  echteGesamt: number;
  /** Zeilen mit gesetztem `Widerruf_am` — **grösser** als `echteGesamt`. */
  roheGesamt: number;
  heute: string;
  zeitraum: Zeitraum;
  vorlagen: Zeitraum[];
};

/**
 * Die Quote je Closer.
 *
 * ⚠️ **Ohne Argumente: rollierende 12 Monate.** `monat` (`YYYY-MM`) sticht
 * `von`/`bis` — Justins *„eventuell mal einen bestimmten Monat"*.
 */
export const widerrufQuote = (
  p: { von?: string; bis?: string; monat?: string; granular?: "m" | "w" } = {}
) =>
  callBackend<QuotenAntwort>("/api/v1/widerruf/quote", p);

/**
 * Eine Quote als Text — **immer mit ihrem Nenner.**
 *
 * 🔑 Justin hat den Mindest-Nenner gekippt (*„immer alle"*). Die Falle dagegen
 * bleibt real: *„1 von 2 = 50 %" steht sonst über „3 von 60 = 5 %".* Deshalb
 * gibt es diese Funktion und keinen nackten Prozentwert — ***eine Quote ohne
 * Nenner sortiert nach Zufall und sieht dabei aus wie eine Rangliste.***
 */
export function quoteText(z: { zaehler: number; nenner: number; quote: number | null }): string {
  if (z.quote === null || z.nenner === 0) return "keine Abschlüsse";
  return `${(z.quote * 100).toFixed(1).replace(".", ",")} % (${z.zaehler} von ${z.nenner})`;
}

export const ZUSTAND_LABEL: Record<WiderrufZustand, string> = {
  eingegangen: "Eingegangen",
  in_bearbeitung: "In Bearbeitung",
  frist_abgelaufen: "Frist abgelaufen",
  ergebnis_gemeldet: "Ergebnis gemeldet",
  // 🔑 Der Text sagt, WORAUF gewartet wird. „Wartet" allein liesse offen, ob
  // jemand arbeitet oder ob etwas liegen geblieben ist.
  wartet_auf_bestaetigung: "Wartet auf Widerrufsbestätigung",
  abgeschlossen: "Abgeschlossen",
  storniert: "Storniert"
};

export const ERGEBNIS_LABEL: Record<WiderrufErgebnis, string> = {
  besteht: "Widerruf besteht",
  zurueckgenommen: "Verein nimmt zurück",
  nicht_erreicht: "Verein nicht erreicht",
  vertrag_angepasst: "Vertrag angepasst"
};

export const PARTEI_LABEL: Record<QuittungPartei, string> = {
  vertrieb: "Vertrieb",
  kb: "Kundenbetreuung",
  finance: "Finance"
};

export const WEG_LABEL: Record<string, string> = {
  rechnung: "Rechnungspostfach",
  partnerbetreuung: "Partnerbetreuung",
  vertragswesen: "Vertragswesen",
  vertrieb: "Vertriebspostfach",
  persoenlich: "persönliche Mail",
  post: "Post",
  telefon: "Telefon",
  sonstige: "sonstiges"
};

// ═══════════════════════════════════════════════════════════════════════════
// DAS SIGNAL IN DER SEITENLEISTE (Justin, 14.09.2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Nur die Anzahl der offenen Vorgänge — für den Zähler am Navigationspunkt.
 *
 * 🔴 **`offen` kann `null` sein**, und das ist keine Nachlässigkeit: der Worker
 * liefert `null`, wenn die Abfrage ausfiel. Die Seitenleiste zeigt dann **keine
 * Zahl** statt einer 0. 🔑 *Eine 0 wäre die Behauptung „nichts offen" — genau
 * die Aussage, die in diesem Moment nicht belegt ist.*
 *
 * ⚠️ Bewusst ein eigener, schlanker Endpunkt statt `widerrufUebersicht()`:
 * die Übersicht rechnet zusätzlich das Rechnungsrisiko über alle Vereine, und
 * diese Zahl wird bei jedem Seitenwechsel gebraucht.
 */
export const widerrufZaehler = () =>
  callBackend<{ offen: number | null; faelle?: WiderrufFall[]; heute: string }>("/api/v1/widerruf/zaehler", {});

// ═══════════════════════════════════════════════════════════════════════════
// VOR ODER NACH DER VEREINSEINRICHTUNG (18.09.2026)
// ═══════════════════════════════════════════════════════════════════════════

export type ModellLage = "ausserhalb" | "vor_ve" | "nach_ve" | "ohne_ve";

export type ModellZeile = {
  fallId: string;
  verein: string;
  eingangAm: string;
  vertragsstart: string;
  veTermin: string;
  /**
   * Ein **vorgemerkter** Termin, wenn es keinen gehaltenen gibt. `""` = keiner.
   * ⛔ Nur zum Beschriften — `lage` rechnet weiter allein an `veTermin`.
   * s. `leseVorgemerkteVe` in `src/lib/ve-termine.ts`.
   */
  veVorgemerkt?: string;
  lage: ModellLage;
};

export type ModellBefund = {
  stichtag: string;
  grundmenge: number;
  vorVe: number;
  nachVe: number;
  ohneVe: number;
  ausserhalb: number;
  /** `vorVe + nachVe` — der Nenner des Anteils. */
  entscheidbar: number;
  /**
   * Anteil „nach der VE" an den ENTSCHEIDBAREN. `null` = nicht messbar, nicht 0.
   * 🔑 Hiess bis 20.09.2026 `quoteNachVe` — s. `src/lib/widerruf-modell.ts`.
   */
  anteilNachVe: number | null;
  /** Warum er `null` ist, in Worten. `""` = er ist gesetzt. */
  nichtMessbarWeil: string;
  zeilen: ModellZeile[];
};

/**
 * Die Aufteilung vor/nach Vereinseinrichtung.
 *
 * ⚠️ Der Stichtag ist eine **Annahme** und steht deshalb im Aufruf, nicht im
 * Code: *eine Annahme, die man nur durch einen Deploy ändern kann, wird zur
 * Tatsache.*
 */
export const widerrufModell = (stichtag = "") =>
  callBackend<{ modell: ModellBefund; weg: WegStufe[]; bestand: BestandBefund; heute: string }>(
    "/api/v1/widerruf/modell", { stichtag }
  );

/** Eine Stufe im Weg eines Widerrufs (19.09.2026). */
export type WegStufe = {
  name: string;
  wert: number;
  /** Bezugsmenge DIESER Stufe — nicht immer die der ersten. */
  nenner: number;
  /** Abgang gegenüber der vorigen Stufe. `null` an der ersten. */
  abgang: number | null;
  /** Steht nur da, wo der Nenner nicht der erste ist. */
  hinweis: string;
};

/** Eine Woche der Bühne „Bestand im Register" (20.09.2026). */
export type BestandWoche = { woche: string; zugang: number; imRegister: number };

export type BestandBefund = {
  wochen: BestandWoche[];
  gesamt: number;
  /** Median der Tage zwischen Erklärung und Erfassung. `null` = nichts zu messen. */
  liegezeitMedian: number | null;
  liegezeitMax: number;
  mitLiegezeit: number;
};

// ═══════════════════════════════════════════════════════════════════════════
// VE-KPI — Lage und Hosts (Endpunkt seit 17.09., Frontend seit 20.09.)
// ═══════════════════════════════════════════════════════════════════════════

export type VeHostQuote = {
  host: string;
  name: string;
  /** Gehaltene Vereinseinrichtungen — der Nenner. */
  nenner: number;
  /** Widerrufe NACH der gehaltenen VE. Nur die danach. */
  zaehler: number;
  quote: number | null;
  /** Noch nicht entschiedene Widerrufe nach der VE. */
  offen: number;
};

export type VeHostBericht = {
  zeilen: VeHostQuote[];
  gesamt: VeHostQuote;
  /** Gehaltene VEs ohne eingetragenen Host — kein Rest, sondern die Lücke. */
  ohneHost: number;
  /** Hosts, deren Kennung im Personalstamm nicht steht. */
  nichtAufgeloest: number;
  /**
   * 🔴 Warum keine Quote dasteht. `""` = sie ist gesetzt.
   * 📏 20.09.2026: **ein** Widerrufsereignis über alle Hosts zusammen.
   * 🔑 Nicht der Nenner je Host ist zu klein, sondern der Zähler insgesamt.
   */
  nichtMessbarWeil: string;
};

/**
 * 🔴 **Der Endpunkt gibt es seit dem 17.09., das Frontend kannte ihn nicht.**
 * Der sechste Fall desselben Musters in dieser Strecke: gerechnet, geliefert,
 * nie gelesen. 🔑 *Ein Endpunkt ohne Aufrufer ist eine Zusage an niemanden.*
 */
export const widerrufVeKpi = () =>
  callBackend<{
    heute: string; hosts: VeHostBericht;
    /**
     * 🆕 25.09.2026 — die Rohzeilen, damit das Frontend den Verlauf je Host in
     * Zeitfaecher teilen kann. Der Nenner ist die gehaltene Einrichtung
     * (`veDatum`), der Zaehler ein Widerruf danach (`widerrufAm`).
     */
    zeilen: Array<{ veDatum: string; veHost: string; widerrufAm: string }>;
  }>("/api/v1/widerruf/veKpi", {});

/**
 * Die eingefrorenen Erfassungswerte berichtigen (B6 + B8, 20.09.2026).
 *
 * ⚠️ **Nur gesetzte Felder werden übergeben.** `undefined` heisst „nicht
 * anfassen", `""` heisst „leeren" — wo beide dasselbe bedeuten, kann man nichts
 * mehr leeren. Deshalb baut der Aufrufer das Objekt selbst auf.
 *
 * ⛔ Der Server rechnet **nichts** nach: keine neue Frist, keine Aufgaben, kein
 * HubSpot. Das zieht der Abgleich beim nächsten Lauf.
 */
export const widerrufErfassungKorrigieren = (
  fallId: string,
  k: {
    eingang_am?: string; vertragsstart?: string;
    frist_ende?: string; frist_quelle?: string; recht_offen?: boolean;
  },
  grund: string
) => callBackend<VorgangAntwort>("/api/v1/widerruf/erfassungKorrigieren", { fall_id: fallId, grund, ...k });

// ── ⑥ STUFE Ⓒ: VORSCHLÄGE BESTÄTIGEN (21.09.2026) ──────────────────────────
//
// 🔴 `verein_vorbehalt` stand seit dem 20.09. mit allen Feldern und hatte
// keinen Zugang ausser SQL. Seit dem 21.09. legt der Vorleser (Ⓑ) täglich
// Vorschläge an — ohne diese Maske könnte sie niemand annehmen.
// 🔑 ***Ein Vorschlag, den niemand bestätigen kann, ist eine Warteschlange,
// die nur wächst.***

export type Vorbehalt = {
  vorbehaltId: string;
  /**
   * 🆕 06.10.2026 (Justin: „Anzeige zusammenfassen") — gesetzt, wenn mehrere
   * Vorschläge mit demselben Verein, derselben Sache und demselben Wert zu EINER
   * Zeile zusammengefasst sind: die IDs ALLER Belege, einschliesslich der eigenen.
   * Bestätigen/Verwerfen gilt dann für jede. Nur in der Ansicht, nie vom Server.
   */
  zusammengefasst?: string[];
  companyId: string;
  /** Anzeigename aus dem Spiegel; `""` = dort (noch) nicht geführt. */
  verein: string;
  sache: string;
  /** Der erhobene Wortlaut, gekürzt — er ZEIGT auf die Quelle, ersetzt sie nicht. */
  aussage: string;
  belegArt: string;
  belegId: string;
  belegAm: string;
  status: string;
  erhobenAm: string;
  bestaetigtVon: string;
  bestaetigtAm: string;
  /** `norm` | `datum` | `keine` */
  rechtArt: string;
  rechtDatum: string;
  /**
   * 🆕 26.09.2026 — die **Kündigungs**-Regel aus derselben Absprache.
   * `'skr_datum' | 'festes_ende' | 'frist_monate'`, leer bei Widerrufs-Zeilen.
   *
   * 🔴 Die Spalten gibt es seit 0130 (heute), der Vorleser füllt sie seit B7,
   * `verein-vorbehalt-store.ts` liest sie — und der Frontend-Typ kannte sie
   * nicht. **Die fünfte Naht dieser Art an einem Tag.**
   * 🔑 *Der Wert liegt an der Quelle und fällt eine Ebene später hin.*
   *
   * ⛔ **Sie rechnen heute NICHTS.** Kein Aufrufer im Worker liest `regel_art`,
   * um ein Vertragsende zu bestimmen — nachgesehen am 26.09.2026, 0 Treffer
   * ausserhalb von Vorleser und Store. Eine Bestätigung hält deshalb die
   * **Aussage** fest, nicht die Wirkung.
   * 🔑 ***Eine Bestätigung, die nichts bewirkt, ist eine Quittung auf eine
   * Handlung, die nicht stattfand*** — deshalb sagt die Ansicht das ausdrücklich.
   */
  regelArt: string;
  /** Bei `skr_datum` und `festes_ende`: `YYYY-MM-DD`. */
  regelDatum: string;
  /** Bei `frist_monate`: die Zahl **als Text** — `''` und `'0'` sind zwei Aussagen. */
  regelMonate: string;
  /**
   * 🆕 21.09.2026 — der IST-Wert, frisch beim Lesen geholt (nicht im
   * Vorbehalt gespeichert: ein Vergleichswert gehoert an den Zeitpunkt des
   * Vergleichs, nicht an den der Erhebung).
   */
  /** 🆕 26.10.2026: `kein_recht` = Bestellung ab 23.10. ohne Satz 3. */
  istArt: "norm" | "datum" | "kein_recht";
  istDatum: string;
  /** `vereinbarung` = steht im Vertragstext · `default` = gilt unwidersprochen. */
  /** J15d (16.10.2026): `absprache` = eine bestätigte Abrede sticht die Vereinbarung. */
  /** 🆕 26.10.2026: `anpassung` = bestätigte Vertragsanpassung · `fassung` = § 7 AGB neu (Satz 3). */
  istQuelle: "vereinbarung" | "default" | "absprache" | "anpassung" | "fassung";
  istBeleg: string;
  /** 🆕 26.10.2026 — Klartext zur Fassung, `""` sonst. */
  istHinweis?: string;
  /**
   * Die Stundenzahl der Absprache (`24`, `48`, …).
   * 🔴 Sie stand seit dem 24.09. in der Tabelle und wurde nie gelesen —
   * `SPALTEN` in `verein-vorbehalt-store.ts` nannte sie nicht.
   */
  stunden: number;
  /**
   * 🆕 25.09.2026 — **der dritte Wert**: die Frist, die nach einer Bestätigung
   * gälte. `""` wenn nicht rechenbar.
   *
   * 🔑 ***„Heute gilt" und „laut Absprache" sind Regeln; entschieden wird über
   * ein Datum.*** Ohne diesen Wert musste jeder ihn im Kopf aus einem VE-Termin
   * rechnen, der gar nicht auf dem Schirm stand.
   */
  wuerdeWert: string;
  /**
   * Woraus sich der Wert ergibt, im Klartext — **auch wenn `wuerdeWert` leer
   * ist**. Dann sagt er, warum nichts gerechnet werden konnte.
   * 🔑 *Eine leere Zelle sieht aus wie ein Fehler; ein Satz sagt, dass niemand
   * einen gemacht hat.*
   */
  wuerdeHerkunft: string;
};

export const widerrufVorbehalteOffen = () =>
  callBackend<{ heute: string; vorbehalte: Vorbehalt[] }>("/api/v1/widerruf/vorbehalteOffen", {});

/**
 * Bestätigen oder verwerfen.
 *
 * ⚠️ **Bestätigen verlangt einen Grund (≥ 10 Zeichen).** Ein bestätigter
 * Vorbehalt darf einen echten Befund unterdrücken — im Verlauf muss stehen,
 * worauf er sich stützt. Verwerfen braucht keinen, hält aber Akteur und Datum
 * fest: *wer verwirft, hat hingesehen.*
 */
export const widerrufVorbehaltStatus = (p: {
  vorbehalt_id: string; status: string; grund?: string;
  /**
   * 🆕 **KORRIGIEREN** (25.09.2026): bestätigen, aber mit einem **dritten**
   * Datum — weder das heute geltende noch das der Absprache.
   *
   * 🔑 ***Wer nur zustimmen oder ablehnen kann, stimmt einem falschen Wert zu,
   * um einen falscheren abzuwehren.***
   *
   * ⚠️ Nur zusammen mit `status: "bestaetigt"`; der Server weist es sonst ab.
   * Der Vorbehalt trägt danach `recht_art = "datum"`.
   */
  recht_datum?: string;
}) =>
  callBackend<{ vorbehalt: Vorbehalt; hinweise: string[] }>("/api/v1/widerruf/vorbehaltStatus", p);
